/**
 * Яндекс.Метрика: один счётчик на лендинг, кабинет, web-booking.
 * Цели (`start_signup`, `registration_success`) создаются вручную в кабинете Метрики.
 */

export const YANDEX_METRIKA_COUNTER_ID = 108190951;
const YANDEX_METRIKA_SCRIPT_ID = 'yandex-metrika-tag';
const YANDEX_METRIKA_SCRIPT_SRC = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRIKA_COUNTER_ID}`;

type YmFn = (counterId: number, method: string, ...args: unknown[]) => void;

function getYm(): YmFn | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { ym?: YmFn }).ym;
}

export function initYandexMetrika(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (document.getElementById(YANDEX_METRIKA_SCRIPT_ID)) return;

  const win = window as Window & {
    ym?: YmFn & { a?: unknown[]; l?: number };
  };

  if (!win.ym) {
    const ymStub = ((...args: unknown[]) => {
      (ymStub.a = ymStub.a || []).push(args);
    }) as YmFn & { a?: unknown[]; l?: number };
    ymStub.l = Date.now();
    win.ym = ymStub;
  }

  const script = document.createElement('script');
  script.id = YANDEX_METRIKA_SCRIPT_ID;
  script.async = true;
  script.src = YANDEX_METRIKA_SCRIPT_SRC;
  document.head.appendChild(script);

  win.ym?.(YANDEX_METRIKA_COUNTER_ID, 'init', {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  });
}

/** JS-цель (событие без смены URL). */
export function reachYandexMetrikaGoal(goalName: string, params?: Record<string, unknown>): void {
  try {
    const ym = getYm();
    if (!ym) return;
    if (params && Object.keys(params).length > 0) {
      ym(YANDEX_METRIKA_COUNTER_ID, 'reachGoal', goalName, params);
    } else {
      ym(YANDEX_METRIKA_COUNTER_ID, 'reachGoal', goalName);
    }
  } catch {
    /* счётчик не загрузился — не ломаем UI */
  }
}

/** Привязка внутреннего id мастера к ClientID (после логина / регистрации). */
export function setYandexMetrikaUserId(userId: string): void {
  try {
    getYm()?.(YANDEX_METRIKA_COUNTER_ID, 'setUserID', userId);
  } catch {
    /* ignore */
  }
}

/** Просмотр виртуальной страницы при SPA-навигации (без полной перезагрузки). */
export function hitYandexMetrika(
  url: string,
  options?: { title?: string; referer?: string }
): void {
  try {
    const ym = getYm();
    if (!ym) return;
    const opts =
      options?.title != null || options?.referer != null
        ? {
            ...(options.title != null ? { title: options.title } : {}),
            ...(options.referer != null ? { referer: options.referer } : {}),
          }
        : undefined;
    if (opts && Object.keys(opts).length > 0) {
      ym(YANDEX_METRIKA_COUNTER_ID, 'hit', url, opts);
    } else {
      ym(YANDEX_METRIKA_COUNTER_ID, 'hit', url);
    }
  } catch {
    /* ignore */
  }
}
